// =============================================================================
// features/meu-whatsapp/connectPrompt.ts — Regra pura do convite "conecte o seu
// WhatsApp" (F56-S03).
//
// O convite é um modal montado no AppLayout: qualquer tela autenticada pode
// abri-lo. Por isso a decisão de abrir mora aqui, em função pura (sem React,
// sem rede), para ser provada em teste — o projeto não renderiza React nos
// testes (ver `hooks/useSyncHistoryAlert.ts`, mesmo desenho).
//
// "Dispensado nesta aba": `sessionStorage` vale por ABA (não por navegador),
// chaveado pelo agente. Fechar a aba (ou abrir outra) libera o convite de novo, enquanto o vendedor não
// conectar. Nunca `localStorage`: dispensar é "agora não", não "nunca mais".
// =============================================================================

import type { MeuStatus } from './useMeuWhatsApp';

/** Flag que liga o pareamento pelo próprio vendedor (F39-S08). */
export const CONNECT_PROMPT_FLAG = 'whatsapp_sync.pairing.enabled';

/** Permissão de quem tem WhatsApp próprio (agente e gestor regional). */
export const CONNECT_PROMPT_PERMISSION = 'channel.self_pair';

/**
 * Rotas onde o convite atrapalha ou é redundante. Vale para a rota e suas
 * subrotas: `/meu-whatsapp` cobre `/meu-whatsapp/inbox`.
 */
export const CONNECT_PROMPT_EXCLUDED_ROUTES: readonly string[] = [
  '/login',
  '/primeiro-acesso',
  '/meu-whatsapp',
];

/** `true` quando `pathname` é uma rota excluída ou uma subrota dela. */
export function isConnectPromptRouteExcluded(pathname: string): boolean {
  return CONNECT_PROMPT_EXCLUDED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}

export interface ConnectPromptInput {
  /** Flag `whatsapp_sync.pairing.enabled` ligada para este usuário. */
  readonly flagEnabled: boolean;
  /** `hasPermission('channel.self_pair')`. */
  readonly hasPermission: boolean;
  /**
   * `GET /api/channels/waha/me` respondeu OK, ou seja, o usuário tem agente
   * vinculado. 404 (sem agente) e 403 (preso ao primeiro acesso) dão `false`.
   */
  readonly hasAgent: boolean;
  /** Estado do aparelho; `null` enquanto a consulta não respondeu. */
  readonly status: MeuStatus | null;
  readonly pathname: string;
  /** Já dispensado nesta aba do navegador. */
  readonly dismissed: boolean;
  /**
   * O modal obrigatório de 1º login (e-mail pessoal, feature account) está ativo
   * ou ainda carregando. Dois modais bloqueantes empilhados não pode: o convite
   * espera. Na dúvida (perfil carregando) conta como pendente — fail-closed.
   */
  readonly personalEmailPending: boolean;
}

/**
 * Decide se o convite deve abrir. Todas as condições precisam valer (AND):
 * fail-closed — na dúvida (consulta pendente, sem dados), não abre.
 */
export function shouldShowConnectWhatsAppPrompt({
  flagEnabled,
  hasPermission,
  hasAgent,
  status,
  pathname,
  dismissed,
  personalEmailPending,
}: ConnectPromptInput): boolean {
  if (!flagEnabled) return false;
  if (!hasPermission) return false;
  if (!hasAgent) return false;
  if (status === null) return false;
  if (status === 'WORKING') return false;
  if (isConnectPromptRouteExcluded(pathname)) return false;
  if (dismissed) return false;
  if (personalEmailPending) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Painel do QR: quando oferecer o botão manual
// ---------------------------------------------------------------------------

export interface ManualRetryInput {
  readonly autoStart: boolean;
  /** Sessão parada (STOPPED/FAILED): precisa de um novo início para haver QR. */
  readonly precisaIniciar: boolean;
  /** A mutation de início está em voo. */
  readonly iniciando: boolean;
  /** O início automático já foi disparado uma vez neste painel. */
  readonly jaIniciou: boolean;
  /** A mutation de início falhou (ex.: 503). */
  readonly falhouAoIniciar: boolean;
}

/**
 * Com `autoStart`, só a PRIMEIRA tentativa é automática. Se depois dela a sessão
 * volta a ficar parada (QR expirou, falhou), reiniciar sozinho em loop seria
 * perigoso: a próxima tentativa tem que ser um clique explícito. O spinner fica
 * só para quando há algo em andamento (mutation pendente ou sessão STARTING).
 */
export function shouldOfferManualRetry({
  autoStart,
  precisaIniciar,
  iniciando,
  jaIniciou,
  falhouAoIniciar,
}: ManualRetryInput): boolean {
  if (!autoStart || !precisaIniciar || iniciando) return false;
  return jaIniciou || falhouAoIniciar;
}

// ---------------------------------------------------------------------------
// Persistência "dispensado nesta aba" — sessionStorage, por agente
// ---------------------------------------------------------------------------

function dismissedStorageKey(agentId: string): string {
  return `bdp:connect-whatsapp-prompt-dismissed:${agentId}`;
}

/**
 * Lê se o convite já foi dispensado nesta aba. Falha para `false`: um
 * `sessionStorage` indisponível (modo privado, quota) mostra o convite de novo
 * em vez de escondê-lo para sempre.
 */
export function hasDismissedConnectPrompt(agentId: string): boolean {
  try {
    return window.sessionStorage.getItem(dismissedStorageKey(agentId)) === '1';
  } catch {
    return false;
  }
}

export function markConnectPromptDismissed(agentId: string): void {
  try {
    window.sessionStorage.setItem(dismissedStorageKey(agentId), '1');
  } catch {
    // sessionStorage indisponível — o estado em memória do hook ainda impede
    // que o convite reabra durante esta sessão da página.
  }
}
